import type { DefaultOptionType, SelectProps } from 'antd/es/select'
import type { IFilterPaymentsResult } from '../../API/payments'

import { Select, Spin } from 'antd'
import { useCallback, useRef, useState } from 'react'

export interface DebounceSelectProps<
  ValueType = string | { value: string; label: string }
> extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions: (search: string) => Promise<IFilterPaymentsResult>
  debounceTimeout?: number
}

function DebounceSelect({
  fetchOptions,
  debounceTimeout = 800,
  onOpenChange,
  ...props
}: DebounceSelectProps) {
  const [fetching, setFetching] = useState(false)
  const [options, setOptions] = useState<DefaultOptionType[]>([])
  const fetchRef = useRef(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const loadOptions = useCallback(
    (value: string) => {
      fetchRef.current += 1
      const fetchId = fetchRef.current
      setOptions([])
      setFetching(true)

      fetchOptions(value).then(newOptions => {
        if (fetchId !== fetchRef.current) {
          return
        }
        const returnOptions: DefaultOptionType[] = []
        newOptions.data.map(item => {
          if (typeof item !== 'string') {
            returnOptions.push({
              key: `${item.groupNumber} | ${item.groupName}`,
              label: `${item.groupNumber} | ${item.groupName}`,
              value: item.groupNumber
            })
          }
        })

        setOptions(returnOptions)
        setFetching(false)
      })
    },
    [fetchOptions]
  )

  const debounceFetcher = useCallback(
    (value: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        loadOptions(value)
      }, debounceTimeout)
    },
    [loadOptions, debounceTimeout]
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open && options.length === 0 && !fetching) {
        loadOptions('')
      }
      onOpenChange?.(open)
    },
    [options.length, fetching, loadOptions, onOpenChange]
  )

  return (
    <Select
      labelInValue
      showSearch
      filterOption={false}
      {...props}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size='small' /> : null}
      onOpenChange={handleOpenChange}
      options={options}
    />
  )
}

export default DebounceSelect
